import { Link } from "react-router-dom";
import { CheckSquare, AlertTriangle, CalendarClock, UserX, CheckCircle2 } from "lucide-react";

export function Stat({ label, value, icon: Icon, tone = "text-slate-50", testid }) {
  return (
    <div className="athanor-card p-4 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${tone}`} />
      </div>
      <div className={`mt-2 tabular-nums text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export function ClickUpStats({ s }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="clickup-stats">
      <Stat label="Aperti" value={s.open} icon={CheckSquare} testid="clickup-stat-open" />
      <Stat label="Scaduti" value={s.overdue} icon={AlertTriangle} tone={s.overdue ? "text-red-400" : "text-slate-50"} testid="clickup-stat-overdue" />
      <Stat label="Oggi" value={s.today} icon={CalendarClock} tone="text-[#C5A059]" testid="clickup-stat-today" />
      <Stat label="Settimana" value={s.week} icon={CalendarClock} testid="clickup-stat-week" />
      <Stat label="Non assegnati" value={s.unassigned} icon={UserX} tone={s.unassigned ? "text-amber-300" : "text-slate-50"} testid="clickup-stat-unassigned" />
      <Stat label="Chiusi 7gg" value={s.closed_7d} icon={CheckCircle2} tone="text-emerald-400" testid="clickup-stat-closed" />
    </div>
  );
}

export function Workload({ rows, title, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.open));
  return (
    <div className="athanor-card p-5 fade-up" data-testid={testid}>
      <h3 className="mb-3 text-sm font-semibold text-slate-100">{title}</h3>
      <div className="space-y-2">
        {rows.slice(0, 8).map((r) => (
          <div key={r.name} className="text-xs">
            <div className="mb-1 flex justify-between text-slate-300"><span className="truncate">{r.name}</span><span className="tabular-nums text-slate-400">{r.open}{r.overdue ? <span className="ml-1 text-red-400">· {r.overdue} scad.</span> : null}</span></div>
            <div className="h-1.5 rounded-full bg-zinc-800"><div className="h-1.5 rounded-full bg-[#C5A059]" style={{ width: `${(r.open / max) * 100}%` }} /></div>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-500">Nessun task aperto.</p>}
      </div>
    </div>
  );
}

// Widget compatto per la Dashboard Marketing
export default function ClickUpWidget({ data }) {
  if (!data) return null;
  if (!data.connected) {
    return (
      <div className="athanor-card p-6 fade-up" data-testid="clickup-widget-empty">
        <h2 className="text-base font-semibold text-slate-100">Team marketing · ClickUp</h2>
        <p className="mt-1 text-base text-slate-400">Collega ClickUp per vedere qui task scaduti, in scadenza e carico del team. <Link to="/clickup" className="text-[#C5A059] hover:underline">Vai a ClickUp →</Link></p>
      </div>
    );
  }
  const s = data.summary;
  const urgent = data.tasks.filter((t) => !t.closed && (t.due_state === "overdue" || t.due_state === "today")).slice(0, 6);
  return (
    <div className="athanor-card p-6 fade-up" data-testid="clickup-widget">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-100">Team marketing · ClickUp</h2>
        <Link to="/clickup" className="inline-flex min-h-11 items-center text-xs text-slate-300 hover:underline md:min-h-0" data-testid="clickup-widget-link">Apri board →</Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Aperti", s.open, ""], ["Scaduti", s.overdue, s.overdue ? "text-red-400" : ""], ["Oggi", s.today, "text-[#C5A059]"], ["Chiusi 7gg", s.closed_7d, "text-emerald-400"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg border border-zinc-800/60 p-3"><div className="text-xs text-slate-500">{l}</div><div className={`tabular-nums text-xl font-bold ${c || "text-slate-50"}`}>{v}</div></div>
        ))}
      </div>
      {urgent.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="clickup-widget-urgent">
          <div className="text-xs text-slate-500">Da fare subito</div>
          {urgent.map((t) => (
            <a key={t.task_id} href={t.url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 rounded-md border border-zinc-800/60 px-3 py-2 text-sm hover:border-[#C5A059]/40">
              <span className="truncate text-slate-100">{t.name}</span>
              <span className={`shrink-0 tabular-nums text-xs ${t.due_state === "overdue" ? "text-red-400" : "text-[#C5A059]"}`}>{(t.assignees || [])[0]?.name.split(" ")[0] || "—"} · {new Date(t.due_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
